import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowRight, Target, ShieldCheck, Wrench, HardHat, Ruler, Cog } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";

export default function AboutPage() {
  usePageMeta("about");
  const navigate = useNavigate();

  const scrollToContact = () => {
    navigate("/#contact");
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onContactClick={scrollToContact} />
      
      <main className="pt-20 md:pt-24">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-black text-white">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20 grayscale"
            style={{
              backgroundImage: "url('https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/wq9mb8f0_IMG_3050%20%281%29.jpeg')",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-6 md:px-12">
            <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
              Construction Cost Engineering & Tender Assurance
            </span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl uppercase mt-4 mb-6">
              Stop Pricing Blind
            </h1>
            <p className="font-body text-xl md:text-2xl text-zinc-300 max-w-3xl leading-relaxed">
              BLACKLINE exists for one reason—to stop builders pricing blind.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 md:py-24 border-t-4 border-black">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
              {/* Left: Story */}
              <div className="space-y-6">
                <p className="font-body text-lg text-zinc-800 leading-relaxed">
                  Most builders don't lose money because they can't build. They lose money because the quote was missing the stuff that hurts.
                </p>
                <p className="font-body text-zinc-600 leading-relaxed">
                  The ugly part is you usually don't find out until you're halfway through the job, the client thinks it was included, you're trying to keep the programme alive, and your margin is quietly getting strangled in the corner.
                </p>
                <p className="font-body text-zinc-600 leading-relaxed">
                  I've spent the last couple of decades on real jobs, not in theory land, across civil construction and high-end residential work where mistakes don't show up as "oops", they show up as weeks of unpaid grind and cashflow stress.
                </p>
                <p className="font-body text-zinc-600 leading-relaxed">
                  Blackline is built to stop that. Turn plans and messy scope into clean numbers, clear boundaries, and a price you can actually stand behind.
                </p>
              </div>

              {/* Right: Photo */}
              <div className="border-4 border-black overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_builder-estimates/artifacts/oslr149q_IMG_2227.JPG"
                  alt="Founder of Blackline Estimating"
                  className="w-full h-auto object-cover grayscale"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-zinc-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
                Experience
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mt-4">
                Built From Doing The Work
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-12 md:gap-16">
              <div>
                <p className="font-body text-zinc-600 leading-relaxed mb-6">
                  My background is a mix of large-scale civil delivery, including major water and wastewater pipeline projects, plus time on architectural builds where detail and sequencing actually matter.
                </p>
                <p className="font-body text-zinc-600 leading-relaxed">
                  I've run sites, managed programmes, lived the reality of labour, logistics, services, access, and ground conditions, plus the thousand little "extras" that never seem to be on the drawings but always end up on your timesheets.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 border-2 border-black bg-white">
                  <HardHat size={24} strokeWidth={1.5} />
                  <span className="font-body">Large-scale civil & pipeline projects</span>
                </div>
                <div className="flex items-center gap-4 p-4 border-2 border-black bg-white">
                  <Ruler size={24} strokeWidth={1.5} />
                  <span className="font-body">High-end residential & architectural builds</span>
                </div>
                <div className="flex items-center gap-4 p-4 border-2 border-black bg-white">
                  <Cog size={24} strokeWidth={1.5} />
                  <span className="font-body">Site & programme management</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What You Get Section */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-black text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="mb-12">
              <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
                Cost Engineering
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mt-4">
                What You Get
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="border border-zinc-800 p-8">
                <Target size={32} strokeWidth={1.5} className="mb-4 text-zinc-400" />
                <h3 className="font-heading font-bold text-xl uppercase mb-3">Trade-Based BOQs</h3>
                <p className="font-body text-zinc-400">Quantities broken down by trade, ready for procurement or subcontractor quotes.</p>
              </div>
              <div className="border border-zinc-800 p-8">
                <Wrench size={32} strokeWidth={1.5} className="mb-4 text-zinc-400" />
                <h3 className="font-heading font-bold text-xl uppercase mb-3">Realistic Labour</h3>
                <p className="font-body text-zinc-400">Labour allowances that hold up on site, not fantasy hours from a spreadsheet.</p>
              </div>
              <div className="border border-zinc-800 p-8">
                <ShieldCheck size={32} strokeWidth={1.5} className="mb-4 text-zinc-400" />
                <h3 className="font-heading font-bold text-xl uppercase mb-3">Clear Boundaries</h3>
                <p className="font-body text-zinc-400">Assumptions and exclusions documented so you know exactly what's in and what's out.</p>
              </div>
            </div>

            <div className="mt-12 pt-12 border-t border-zinc-800">
              <blockquote className="font-heading font-bold text-2xl md:text-3xl uppercase text-center">
                No fluff. No mystery scope. No pricing by hope.
              </blockquote>
            </div>

            {/* Boundary Statement */}
            <div className="mt-8 p-4 border border-zinc-700 bg-zinc-900">
              <p className="font-body text-zinc-400 text-sm text-center">
                <strong className="text-zinc-300">Note:</strong> Blackline provides estimating and cost engineering services. Our services do not replace project-specific professional advice from qualified engineers, architects, quantity surveyors, or legal professionals.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 border-t-4 border-black bg-zinc-50">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase mb-6">
              Ready to Price With Confidence?
            </h2>
            <p className="font-body text-zinc-600 mb-8 max-w-2xl mx-auto">
              If you want a BOQ, a labour allowance, a simple programme, or a full estimate that doesn't leave you exposed, upload the plans and we'll come back with scope, fee, and turnaround.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-black text-white h-14 px-8 uppercase font-bold tracking-widest border-2 border-black btn-brutal inline-flex items-center gap-3"
              data-testid="about-cta"
            >
              Get A Quote
              <ArrowRight size={20} />
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
